/**
 * Maszyna stanów animacji awatara — czysta logika (fake clock w testach).
 * PLAN-V2 §2.3 + WAVE5 W3: idle breath, walk cycle, happy micro-react, reduced-motion.
 */

export type AvatarAction = 'sit' | 'jump' | 'wave' | 'eat' | 'swim' | 'sleep' | 'happy';

export type AvatarPose =
  | 'idle'
  | 'walkA'
  | 'walkB'
  | AvatarAction;

export const AVATAR_ACTIONS: readonly AvatarAction[] = [
  'sit',
  'jump',
  'wave',
  'eat',
  'swim',
  'sleep',
  'happy',
] as const;

/** Czas trwania akcji (s) zanim wróci do idle/walk. */
export const ACTION_DURATION: Record<AvatarAction, number> = {
  sit: 1.5,
  jump: 0.65,
  wave: 1.0,
  eat: 1.4,
  swim: 1.2,
  sleep: 2.0,
  /** Krótka mikroreakcja (zakup / głaskanie / nagroda). */
  happy: 0.55,
};

/** Tempo wymiany walkA/walkB (PLAN §2.3). */
export const WALK_STEP_SEC = 0.13;

const NOMINAL_WALK_SPEED = 180;
const SLOW_WALK_STEP_SEC = 0.19;
const FAST_WALK_STEP_SEC = 0.1;

/** Próg |v| (px/s) powyżej którego uznajemy ruch — używany po stronie avatara. */
export const MOVE_SPEED_THRESHOLD = 4;

/** Skala juice przy reduced-motion (0 = wyłączony sok). */
export const REDUCED_MOTION_JUICE_SCALE = 0;

export type AnimJuice = {
  /** Przesunięcie pionowe bazy (idle-bob / skok). */
  bobY: number;
  /** Skala X (squash). */
  scaleX: number;
  /** Skala Y (stretch / oddech). */
  scaleY: number;
  /** Przechylenie w stopniach (± tilt chodu). */
  tiltDeg: number;
  /** Skala cienia X. */
  shadowScaleX: number;
  /** Skala cienia Y. */
  shadowScaleY: number;
  /** Czy sypać kurz pod łapami. */
  dust: boolean;
  /** Intensywność cząsteczek serc/gwiazdek (0–1). */
  sparkle: number;
};

export type AnimMachineState = {
  pose: AvatarPose;
  action: AvatarAction | null;
  /** Pozostały czas akcji (s); 0 = brak. */
  actionRemaining: number;
  moving: boolean;
  /** Rzeczywista prędkość lokomocji w px/s — steruje kadencją i juice. */
  moveSpeed: number;
  walkTimer: number;
  walkFrame: 0 | 1;
  bobT: number;
  /** Faza po starcie/stopie do squash (0–1 wygasa). */
  startStopT: number;
  wasMoving: boolean;
  /** Preferencja dostępności — tłumi bob/tilt/squash/dust/sparkle. */
  reducedMotion: boolean;
  juice: AnimJuice;
};

export type AnimMachineOpts = {
  reducedMotion?: boolean;
};

const IDLE_JUICE: AnimJuice = {
  bobY: 0,
  scaleX: 1,
  scaleY: 1,
  tiltDeg: 0,
  shadowScaleX: 1,
  shadowScaleY: 1,
  dust: false,
  sparkle: 0,
};

export function createAnimMachine(opts: AnimMachineOpts = {}): AnimMachineState {
  return {
    pose: 'idle',
    action: null,
    actionRemaining: 0,
    moving: false,
    moveSpeed: NOMINAL_WALK_SPEED,
    walkTimer: 0,
    walkFrame: 0,
    bobT: 0,
    startStopT: 0,
    wasMoving: false,
    reducedMotion: opts.reducedMotion === true,
    juice: { ...IDLE_JUICE },
  };
}

export function setReducedMotion(
  state: AnimMachineState,
  reducedMotion: boolean,
): AnimMachineState {
  if (state.reducedMotion === reducedMotion) return state;
  return { ...state, reducedMotion };
}

export function setMoving(state: AnimMachineState, moving: boolean): AnimMachineState {
  if (state.moving === moving) return state;
  const startStopT = state.moving !== moving ? 0.22 : state.startStopT;
  return { ...state, moving, startStopT, wasMoving: state.moving };
}

export function setMoveSpeed(state: AnimMachineState, speedPxPerSec: number): AnimMachineState {
  const next = Number.isFinite(speedPxPerSec) ? Math.max(0, speedPxPerSec) : 0;
  if (Math.abs(state.moveSpeed - next) < 0.01) return state;
  return { ...state, moveSpeed: next };
}

/**
 * Kadencja kroku zakotwiczona w 180 px/s = dotychczasowe 0.13 s.
 * Wolny spacer jest czytelniejszy, a szybki ruch energiczny bez migotania.
 */
export function walkStepSecForSpeed(speedPxPerSec: number): number {
  const speed = Number.isFinite(speedPxPerSec) ? Math.max(0, speedPxPerSec) : 0;
  if (speed <= NOMINAL_WALK_SPEED) {
    const t = speed / NOMINAL_WALK_SPEED;
    return SLOW_WALK_STEP_SEC + (WALK_STEP_SEC - SLOW_WALK_STEP_SEC) * t;
  }
  const t = Math.min(1, (speed - NOMINAL_WALK_SPEED) / 100);
  return WALK_STEP_SEC + (FAST_WALK_STEP_SEC - WALK_STEP_SEC) * t;
}

function poseForLocomotion(state: AnimMachineState): AvatarPose {
  if (!state.moving) return 'idle';
  return state.walkFrame === 0 ? 'walkA' : 'walkB';
}

export function playIdle(state: AnimMachineState): AnimMachineState {
  return {
    ...state,
    action: null,
    actionRemaining: 0,
    pose: poseForLocomotion(state),
  };
}

export function startAction(state: AnimMachineState, action: AvatarAction): AnimMachineState {
  return {
    ...state,
    action,
    actionRemaining: ACTION_DURATION[action],
    pose: action,
    // akcja wstrzymuje chód wizualnie (ruch świata może trwać — pose = akcja)
  };
}

export function isActionPlaying(state: AnimMachineState): boolean {
  return state.action != null && state.actionRemaining > 0;
}

/**
 * Krok maszyny o dt sekund (fake clock: wołaj z dowolnym dt).
 * Zwraca nowy stan (immutable).
 */
export function stepAnim(state: AnimMachineState, dtSec: number): AnimMachineState {
  const dt = Math.max(0, dtSec);
  let s: AnimMachineState = {
    ...state,
    juice: { ...state.juice },
  };

  // Akcja jednorazowa
  if (s.action != null && s.actionRemaining > 0) {
    const rem = s.actionRemaining - dt;
    if (rem <= 0) {
      s = {
        ...s,
        action: null,
        actionRemaining: 0,
        pose: poseForLocomotion(s),
      };
    } else {
      s = { ...s, actionRemaining: rem, pose: s.action };
      s.juice = applyReducedMotion(juiceForAction(s), s.reducedMotion);
      s = decayStartStop(s, dt);
      return s;
    }
  }

  // Chód / idle
  if (s.moving) {
    const walkStepSec = walkStepSecForSpeed(s.moveSpeed);
    let walkTimer = s.walkTimer + dt;
    let walkFrame = s.walkFrame;
    // płynny cykl: faza 0..1 w obrębie kroku
    while (walkTimer >= walkStepSec) {
      walkTimer -= walkStepSec;
      walkFrame = walkFrame === 0 ? 1 : 0;
    }
    const stepPhase01 = walkTimer / walkStepSec;
    const speedRatio = Math.min(1.4, Math.max(0.25, s.moveSpeed / NOMINAL_WALK_SPEED));
    s = {
      ...s,
      walkTimer,
      walkFrame,
      pose: walkFrame === 0 ? 'walkA' : 'walkB',
      bobT: s.bobT + dt * (8 + speedRatio * 6),
    };
    // bujanie + tilt nóg zsynchronizowane z klatką
    const legSign = walkFrame === 0 ? 1 : -1;
    const contact = Math.sin(stepPhase01 * Math.PI);
    const bob =
      contact * (1.4 + speedRatio * 1.2) +
      Math.sin(s.bobT) * (0.25 + speedRatio * 0.3);
    const tilt = legSign * (1.8 + speedRatio * 2 + contact * speedRatio);
    s.juice = {
      bobY: bob,
      scaleX: 1 + contact * (0.008 + speedRatio * 0.012),
      scaleY: 1 - contact * (0.006 + speedRatio * 0.01),
      tiltDeg: tilt,
      shadowScaleX: 1 - contact * 0.04 * speedRatio,
      shadowScaleY: 1 + contact * 0.02,
      dust:
        s.moveSpeed >= 110 &&
        (walkTimer < walkStepSec * 0.08 || contact > 0.65),
      sparkle: 0,
    };
  } else {
    s = {
      ...s,
      walkTimer: 0,
      pose: 'idle',
      bobT: s.bobT + dt * 2.6,
    };
    // delikatne oddychanie: bob + lekki scaleY
    const breath = Math.sin(s.bobT);
    s.juice = {
      bobY: breath * 1.8,
      scaleX: 1 + breath * 0.012,
      scaleY: 1 - breath * 0.02,
      tiltDeg: 0,
      shadowScaleX: 1 + breath * 0.02,
      shadowScaleY: 1,
      dust: false,
      sparkle: 0,
    };
  }

  s = decayStartStop(s, dt);
  // squash przy starcie/stopie
  if (s.startStopT > 0 && !s.reducedMotion) {
    const t = s.startStopT / 0.22;
    const squash = Math.sin((1 - t) * Math.PI) * 0.12;
    s.juice = {
      ...s.juice,
      scaleX: s.juice.scaleX + squash,
      scaleY: s.juice.scaleY - squash,
    };
  }

  s.juice = applyReducedMotion(s.juice, s.reducedMotion);
  return s;
}

function applyReducedMotion(juice: AnimJuice, reduced: boolean): AnimJuice {
  if (!reduced) return juice;
  return {
    bobY: 0,
    scaleX: 1,
    scaleY: 1,
    tiltDeg: 0,
    shadowScaleX: 1,
    shadowScaleY: 1,
    dust: false,
    sparkle: 0,
  };
}

function decayStartStop(s: AnimMachineState, dt: number): AnimMachineState {
  if (s.startStopT <= 0) return s;
  return { ...s, startStopT: Math.max(0, s.startStopT - dt) };
}

function juiceForAction(s: AnimMachineState): AnimJuice {
  const action = s.action!;
  const total = ACTION_DURATION[action];
  const progress = 1 - s.actionRemaining / total; // 0→1

  switch (action) {
    case 'jump': {
      // parabola bob + stretch w górze + squash lądowanie
      const arc = Math.sin(progress * Math.PI);
      const land = progress > 0.85 ? Math.sin(((progress - 0.85) / 0.15) * Math.PI) * 0.18 : 0;
      return {
        bobY: -arc * 28,
        scaleX: 1 - arc * 0.08 + land,
        scaleY: 1 + arc * 0.12 - land,
        tiltDeg: 0,
        shadowScaleX: 1 - arc * 0.35,
        shadowScaleY: 1 - arc * 0.35,
        dust: progress > 0.9,
        sparkle: 0,
      };
    }
    case 'eat': {
      const chomp = Math.sin(progress * Math.PI * 6) * 0.06;
      return {
        bobY: 4 + Math.abs(chomp) * 8,
        scaleX: 1 + chomp,
        scaleY: 1 - chomp * 0.5,
        tiltDeg: 8,
        shadowScaleX: 1.05,
        shadowScaleY: 1,
        dust: false,
        sparkle: 0.3,
      };
    }
    case 'happy': {
      // krótki podskok (2 bouncy) + sparkle
      const bounce = Math.abs(Math.sin(progress * Math.PI * 2));
      return {
        bobY: -bounce * 12,
        scaleX: 1 + bounce * 0.07,
        scaleY: 1 - bounce * 0.05,
        tiltDeg: Math.sin(progress * Math.PI * 2) * 5,
        shadowScaleX: 1 - bounce * 0.12,
        shadowScaleY: 1,
        dust: false,
        sparkle: 1 - progress * 0.3,
      };
    }
    case 'wave': {
      return {
        bobY: Math.sin(progress * Math.PI * 2) * 3,
        scaleX: 1,
        scaleY: 1,
        tiltDeg: Math.sin(progress * Math.PI * 4) * 5,
        shadowScaleX: 1,
        shadowScaleY: 1,
        dust: false,
        sparkle: 0.2,
      };
    }
    case 'sit': {
      return {
        bobY: 6,
        scaleX: 1.04,
        scaleY: 0.96,
        tiltDeg: 0,
        shadowScaleX: 1.1,
        shadowScaleY: 1,
        dust: false,
        sparkle: 0,
      };
    }
    case 'sleep': {
      return {
        bobY: 10 + Math.sin(progress * Math.PI * 2) * 1.5,
        scaleX: 1.06,
        scaleY: 0.92,
        tiltDeg: -12,
        shadowScaleX: 1.15,
        shadowScaleY: 1,
        dust: false,
        sparkle: 0,
      };
    }
    case 'swim': {
      return {
        bobY: Math.sin(progress * Math.PI * 4) * 4,
        scaleX: 1.02,
        scaleY: 0.98,
        tiltDeg: Math.sin(progress * Math.PI * 2) * 3,
        shadowScaleX: 0.7,
        shadowScaleY: 0.7,
        dust: false,
        sparkle: 0.15,
      };
    }
    default:
      return { ...IDLE_JUICE };
  }
}

/** Klucz tekstury klatki; brak assetu obsługuje renderer (fallback idle/procedural). */
export function poseTextureKey(breed: string, pose: AvatarPose | string): string {
  return `dog_${breed}_${pose}`;
}

/** Kolejność fallbacków tekstury bazy. */
export function baseTextureFallbackChain(breed: string, pose: string): string[] {
  const keys = [poseTextureKey(breed, pose)];
  if (pose !== 'idle') keys.push(poseTextureKey(breed, 'idle'));
  keys.push('ph_dog', 'dog');
  return keys;
}

/**
 * Czy OS/przeglądarka prosi o reduced motion.
 * Bezpieczne w Node (testy) — zwraca false bez window.
 */
export function detectPrefersReducedMotion(): boolean {
  try {
    if (typeof globalThis === 'undefined') return false;
    const g = globalThis as { matchMedia?: (q: string) => { matches: boolean } };
    if (typeof g.matchMedia !== 'function') return false;
    return g.matchMedia('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}
